import { createReducer, on } from '@ngrx/store';
import { ActiveComponentChanged } from 'src/app/shared/actions/eliqua.actions';
import {
  AddNewConfEditData,
  AttributeCodeChanged,
  AttributeTypeChanged,
  ButtonPointerEventDisabled,
  ButtonPointerEventEnabled,
  ConfigurationDescriptionChanged,
  ConfigurationNameChanged,
  DataDelimiterChanged,
  DropZoneDisabled,
  DropZoneEnabled,
  EmptyAddNewConf,
  FileNameChanged,
  FileToUploadChanged,
  FileTypeChanged,
  FirstLineHeadersChanged,
  FlowNameChanged,
  GetAttributesTypesSuccess,
  GetCouponsAttributesSuccess,
  GetDisplayedColumns,
  GetElementsSuccess,
  GetFile,
  GetFileTableRows,
  GetFileTypesSuccess,
  GetFlowNamesSuccess,
  GetLoyaltyMemberAttributesSuccess,
  GetOriginalFile,
  GetPersonsAttributesSuccess,
  MappingOptionsChanged,
  ModelOptionChanged,
  ModelPropertyChanged,
  NotificationEmailChanged,
  ProfileModelChanged,
  ProfileModelKeyChanged,
  RemoveFile,
  ResetToOriginalTable,
  SQLStatementChanged,
  StartNewBulkOperationChanged,
  TableAttributeCodeChanged,
  TableAttributeTypeChanged,
  TableExtraFieldCodeChanged,
  UploadedFileChanged,
} from '../actions/bulk-add-new.actions';
import { GetProfilesSuccess } from '../actions/search-bulk.actions';
import { CloseSidenav } from '../actions/side-nav.actions';
import { BulkAddNewState } from '../models/bulk.models';

const initialState: BulkAddNewState = {
  profiles: [],
  mapModelToKeys: '',
  profileModelValues: [],
  profileModel: '',

  editData: '',
  buttonEventsDisabled: false,
  enteredDropZone: false,
  uploadedFile: { name: '', size: '' },
  fileToUpload: '',
  originalFile: '',
  file: '',
  originalFileTableRows: '',
  originalDisplayedColumns: [],
  fileTableRows: '',
  displayedColumns: [],
  fileName: '',
  fileTypes: [],
  fileType: '',
  delimiters: {
    comma: ',',
    'semi-colon': ';',
    tab: '\t',
    'vertical-bar': '|',
  },
  selectedDelimiter: '',
  firstLineHeaders: false,
  profileModelKey: '',
  attributeTypeValues: [],
  attributeType: '',
  attributeCode: '',
  flowNameValues: [],
  flowName: '',
  personsValues: '',
  loyaltyMemberValues: '',
  couponsValues: '',
  advancedMappingOptions: false,
  firstRowLength: 0,
  sqlStatements: [],
  couponSQLStatements: [],
  attributeSQLStatements: [],
  modelOptions: {
    Option1: 'Model property',
    Option2: 'Attribute',
    Option3: 'Extra field',
  },
  modelProperties: [],
  originalHeaders: '',
  headers: {},
  maxPreviewLines: 5,

  configurationName: '',
  configurationDescription: '',
  notificationEmail: '',
  showNotificationEmailErrorMessage: false,
  startNewBulkOperation: false,
  deleteFlag: false,
  dryRun: false,
};

export const BulkAddNewReducer = createReducer(
  initialState,
  on(GetProfilesSuccess, (state, { res }) => {
    const profileModelValues = res.profiles.map((profiles: any) => [
      profiles.profileCode,
      profiles.profileModelList[0].modelName,
    ]);

    const mapModelToKeys = res.profiles.reduce((mapModelToKeys: any, profiles: any) => {
      mapModelToKeys[profiles.profileCode] = profiles.profileModelList[0].modelSupportedKeys;
      return mapModelToKeys;
    }, {});

    return {
      ...state,
      profiles: res.profiles,
      mapModelToKeys,
      profileModelValues,
    };
  }),
  on(GetFileTypesSuccess, (state, { res }) => ({
    ...state,
    fileTypes: res.fileTypes,
  })),
  on(GetAttributesTypesSuccess, (state, { res }) => ({
    ...state,
    attributeTypeValues: res,
  })),
  on(GetFlowNamesSuccess, (state, { res }) => ({
    ...state,
    flowNameValues: res,
  })),
  on(GetElementsSuccess, (state, { res }) => ({
    ...state,
    sqlStatements: res,
    couponSQLStatements: [res[1]],
    attributeSQLStatements: res.filter((val: string) => val !== 'INSERT_LEGACY'),
  })),
  on(FileToUploadChanged, (state, { fileToUpload }) => ({
    ...state,
    fileToUpload,
  })),
  on(GetFile, (state, { file }) => ({
    ...state,
    file,
  })),
  on(RemoveFile, (state) => ({
    ...state,
    file: '',
    uploadedFile: { name: '', size: '' },
    fileTableRows: '',
    displayedColumns: [],
    fileName: '',
    headers: {},
    firstRowLength: 0,
  })),
  on(GetOriginalFile, (state, { originalFile }) => ({
    ...state,
    originalFile,
  })),
  on(GetFileTableRows, (state, { fileTableRows }) => ({
    ...state,
    fileTableRows,
  })),
  on(GetDisplayedColumns, (state, { cols, firstRowLength }) => {
    let displayedColumns: string[] = Array.from({ length: cols }, (_, index) => `col${index}`);
    const headers = displayedColumns.reduce((acc: any, col: string, index: number) => {
      acc[col] =
        index < firstRowLength
          ? state.originalDisplayedColumns.length && index < state.originalDisplayedColumns.length
            ? {
                sqlStatements: state.originalHeaders['col' + index].sqlStatements,
                sqlStatement: state.originalHeaders['col' + index].sqlStatement,
                selectedModelOption: state.originalHeaders['col' + index].selectedModelOption,
                selectedModelProperty: state.originalHeaders['col' + index].selectedModelProperty,
                attributeType: state.originalHeaders['col' + index].attributeType,
                attributeCode: state.originalHeaders['col' + index].attributeCode,
                extraFieldCode: state.originalHeaders['col' + index].extraFieldCode,
              }
            : {
                sqlStatements: state.sqlStatements,
                sqlStatement: 'INSERT_UPDATE',
                selectedModelOption: 'Model property',
                selectedModelProperty: '',
                attributeType: '',
                attributeCode: '',
                extraFieldCode: '',
              }
          : '';
      return acc;
    }, {});

    return {
      ...state,
      displayedColumns,
      headers,
      firstRowLength,
    };
  }),
  on(FlowNameChanged, (state, { flowName }) => ({
    ...state,
    flowName,
  })),
  on(AttributeTypeChanged, (state, { attributeType }) => ({
    ...state,
    attributeType,
  })),
  on(ButtonPointerEventEnabled, (state) => ({
    ...state,
    buttonEventsDisabled: false,
  })),
  on(ButtonPointerEventDisabled, (state) => ({
    ...state,
    buttonEventsDisabled: true,
  })),
  on(DropZoneEnabled, (state) => ({
    ...state,
    enteredDropZone: true,
  })),
  on(DropZoneDisabled, (state) => ({
    ...state,
    enteredDropZone: false,
  })),
  on(AttributeCodeChanged, (state, { attributeCode }) => ({
    ...state,
    attributeCode,
  })),
  on(GetPersonsAttributesSuccess, (state, { res }) => ({
    ...state,
    personsValues: res.payload.data,
  })),
  on(GetCouponsAttributesSuccess, (state, { res }) => ({
    ...state,
    couponsValues: res.payload.data,
  })),
  on(GetLoyaltyMemberAttributesSuccess, (state, { res }) => ({
    ...state,
    loyaltyMemberValues: res.payload.data,
  })),
  on(UploadedFileChanged, (state, { name, size }) => ({
    ...state,
    uploadedFile: { name: name, size: size },
  })),
  on(FileNameChanged, (state, { fileName }) => ({
    ...state,
    fileName,
  })),
  on(FileTypeChanged, (state, { fileType }) => ({
    ...state,
    fileType,
    selectedDelimiter: '',
  })),
  on(DataDelimiterChanged, (state, { selectedDelimiter }) => ({
    ...state,
    selectedDelimiter,
  })),
  on(FirstLineHeadersChanged, (state) => ({
    ...state,
    firstLineHeaders: !state.firstLineHeaders,
  })),
  on(ProfileModelKeyChanged, (state, { profileModelKey }) => ({
    ...state,
    profileModelKey,
    attributeType: '',
    attributeCode: '',
  })),
  on(MappingOptionsChanged, (state) => ({
    ...state,
    advancedMappingOptions: !state.advancedMappingOptions,
  })),
  on(StartNewBulkOperationChanged, (state) => ({
    ...state,
    startNewBulkOperation: !state.startNewBulkOperation,
  })),
  on(ModelOptionChanged, (state, { selectedModelOption, index }) => ({
    ...state,
    headers: {
      ...state.headers,
      ['col' + index]: {
        ...state.headers['col' + index],
        sqlStatements:
          selectedModelOption === 'Model property'
            ? state.sqlStatements
            : state.profileModel === 'COUPON'
              ? state.couponSQLStatements
              : state.attributeSQLStatements,
        sqlStatement: 'INSERT_UPDATE',
        selectedModelOption,
        selectedModelProperty: '',
        attributeType: '',
        attributeCode: '',
        extraFieldCode: '',
      },
    },
  })),
  on(SQLStatementChanged, (state, { sqlStatement, index }) => ({
    ...state,
    headers: {
      ...state.headers,
      ['col' + index]: {
        ...state.headers['col' + index],
        sqlStatement,
      },
    },
  })),
  on(ModelPropertyChanged, (state, { selectedModelProperty, index }) => {
    let sqlStatements: string[] = state.sqlStatements;
    let sqlStatement: string = state.headers['col' + index].sqlStatement;

    state.profiles.map((profile: any) => {
      if (profile.profileCode === state.profileModel) {
        if (selectedModelProperty in profile.profileModelList[0].fieldActionsMap) {
          sqlStatements = profile.profileModelList[0].fieldActionsMap[selectedModelProperty];
          if (sqlStatements.length === 1) {
            sqlStatement = sqlStatements[0];
          } else if (sqlStatements.includes('INSERT_UPDATE')) sqlStatement = 'INSERT_UPDATE';
          else if (sqlStatements.length === 0) sqlStatement = '';
        }
      }
    });

    return {
      ...state,
      headers: {
        ...state.headers,
        ['col' + index]: {
          ...state.headers['col' + index],
          selectedModelProperty,
          sqlStatements,
          sqlStatement,
        },
      },
    };
  }),
  on(TableAttributeTypeChanged, (state, { attributeType, index }) => ({
    ...state,
    headers: {
      ...state.headers,
      ['col' + index]: {
        ...state.headers['col' + index],
        attributeType,
      },
    },
  })),
  on(TableAttributeCodeChanged, (state, { attributeCode, index }) => ({
    ...state,
    headers: {
      ...state.headers,
      ['col' + index]: {
        ...state.headers['col' + index],
        attributeCode,
      },
    },
  })),
  on(TableExtraFieldCodeChanged, (state, { extraFieldCode, index }) => ({
    ...state,
    headers: {
      ...state.headers,
      ['col' + index]: {
        ...state.headers['col' + index],
        extraFieldCode,
      },
    },
  })),
  on(ConfigurationNameChanged, (state, { configurationName }) => ({
    ...state,
    configurationName,
  })),
  on(ProfileModelChanged, (state, { profileModel }) => {
    const modelProperties = state.profiles.reduce((acc: string[], profile: any) => {
      if (profile.profileCode === profileModel) {
        acc = profile.profileModelList[0].modelFields;
      }
      return acc;
    }, []);

    let headers: any = '';
    if (state.displayedColumns) {
      headers = state.displayedColumns.reduce((acc: any, col: string, index: number) => {
        acc[col] =
          index < state.firstRowLength
            ? {
                sqlStatements: state.sqlStatements,
                sqlStatement: 'INSERT_UPDATE',
                selectedModelOption: 'Model property',
                selectedModelProperty: '',
                attributeType: '',
                attributeCode: '',
                extraFieldCode: '',
              }
            : '';
        return acc;
      }, {});
    }

    return {
      ...state,
      profileModel: profileModel ?? '',
      profileModelKey: '',
      modelProperties,
      headers,
      attributeType: '',
      attributeCode: '',
      flowName: '',
    };
  }),
  on(ConfigurationDescriptionChanged, (state, { configurationDescription }) => ({
    ...state,
    configurationDescription,
  })),
on(NotificationEmailChanged, (state, { notificationEmail }) => ({
  ...state,
  notificationEmail,
  showNotificationEmailErrorMessage: 
    (notificationEmail && notificationEmail.trim() !== '') 
      ? !/^[a-zA-Z0-9._%+-]*[a-zA-Z0-9_%+-]@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(notificationEmail) 
      : false 
})),
  on(AddNewConfEditData, (state, { row }) => {
    let displayedColumns: string[] = Array.from(
      {
        length: row.importConfigurationDto.importMapping.filter(
          (column: any) => !!column.profileColumnName || !!column.attributeCode || !!column.flowFieldCode,
        ).length,
      },
      (_, index) => `col${index}`,
    );
    const headers = displayedColumns.reduce((acc: any, col: string, index: number) => {
      acc[col] =
        index < row.importConfigurationDto.importMapping.length
          ? {
              sqlStatements: row.importConfigurationDto.importMapping[index].profileColumnName.includes(
                row.importConfigurationDto.modelProfile.profileModelList[0].fieldActionsMap,
              )
                ? row.importConfigurationDto.modelProfile.profileModelList[0].fieldActionsMap[
                    row.importConfigurationDto.importMapping[index].profileColumnName
                  ]
                : row.importConfigurationDto.importMapping[index].attribute
                  ? state.profileModel === 'COUPON'
                    ? state.couponSQLStatements
                    : state.attributeSQLStatements
                  : state.sqlStatements,
              sqlStatement: row.importConfigurationDto.importMapping[index].columnStatus,
              selectedModelOption: row.importConfigurationDto.importMapping[index].attribute
                ? 'Attribute'
                : row.importConfigurationDto.importMapping[index].flowField
                  ? 'Extra field'
                  : 'Model property',
              selectedModelProperty: row.importConfigurationDto.importMapping[index].profileColumnName,
              attributeType: row.importConfigurationDto.importMapping[index].attributeType,
              attributeCode: row.importConfigurationDto.importMapping[index].attributeCode,
              extraFieldCode: row.importConfigurationDto.importMapping[index].flowFieldCode,
            }
          : '';
      return acc;
    }, {});

    let fileTableRows: string[][] = Array.from({ length: 5 }, () => []);
    Array.from({ length: 5 }, (_, rowIndex) => {
      displayedColumns.map((column, index) => {
        const cellData = row.importConfigurationDto.importMapping[index].profileColumnName
          ? row.importConfigurationDto.importMapping[index].profileColumnName
          : row.importConfigurationDto.importMapping[index].attributeCode
            ? row.importConfigurationDto.importMapping[index].attributeCode
            : row.importConfigurationDto.importMapping[index].flowFieldCode;
        !rowIndex
          ? fileTableRows[rowIndex].push(`${(cellData as string)?.toLowerCase()}`)
          : fileTableRows[rowIndex].push(`${(cellData as string)?.toLowerCase()} ${rowIndex}`);
      });
    });

    let originalFile = fileTableRows.map((row) => row.join(','));

    return {
      ...state,
      profileModel: row.importModelProfile,
      editData: row,
      fileName: row.importConfigurationDto.fileNamePattern,
      fileType: row.importFileType,
      selectedDelimiter: row.importConfigurationDto.importFileType.config,
      firstLineHeaders: row.importConfigurationDto.withHeader,
      profileModelKey: row.importConfigurationDto.modelProfile.profileModelList[0].modelKey,
      attributeType:
        row.importConfigurationDto.modelProfile.profileModelList[0]?.modelAttributeKey?.attributeType ?? '',
      attributeCode:
        row.importConfigurationDto.modelProfile.profileModelList[0]?.modelAttributeKey?.attributeName ?? '',
      flowName: row.importConfigurationDto.modelProfile.profileModelList[0].flowId ?? '',
      configurationName: row.importConfigurationDto.importConfigurationTitle,
      configurationDescription: row.importConfigurationDto.importConfigurationDescription,
      notificationEmail: row.importConfigurationDto.emailNotification ?? '',
      displayedColumns,
      headers,
      fileTableRows,
      modelProperties: row.importConfigurationDto.modelProfile.profileModelList[0].modelFields,
      deleteFlag: row.deleteFlag,
      dryRun: row.importConfigurationDto.dryRun,
      originalDisplayedColumns: displayedColumns,
      originalFileTableRows: fileTableRows,
      originalHeaders: headers,
      originalFile,
    };
  }),
  on(ResetToOriginalTable, (state) => ({
    ...state,
    headers: state.originalHeaders,
    fileTableRows: state.originalFileTableRows,
    displayedColumns: state.originalDisplayedColumns,
  })),
  on(ActiveComponentChanged, EmptyAddNewConf, CloseSidenav, (state) => ({
    ...state,
    profileModel: '',

    editData: '',
    uploadedFile: { name: '', size: '' },
    originalFile: '',
    file: '',
    fileTableRows: '',
    displayedColumns: [],
    fileName: '',
    fileType: '',
    selectedDelimiter: '',
    firstLineHeaders: false,
    profileModelKey: '',
    attributeType: '',
    attributeCode: '',
    flowName: '',
    advancedMappingOptions: false,
    firstRowLength: 0,
    modelProperties: [],
    headers: {},
    configurationName: '',
    configurationDescription: '',
    startNewBulkOperation: false,
    deleteFlag: false,
    originalFileTableRows: '',
    originalDisplayedColumns: [],
    originalHeaders: '',
    notificationEmail: ''
  })),
);
